import {
  REFERRAL_FRIEND_BONUS_PERCENT,
  REFERRAL_INVITER_PERCENT_BY_PLAN,
} from "./referral";
import { SOCIAL_LINKS } from "./brandAssets";

/**
 * Per-channel share presets for Invite & Earn share preview modal.
 */
export function buildChannelSharePresets({
  inviteUrl,
  inviterName = "I",
  planKey = "free",
  toolLabel = "LazyStudio",
  toolBlurb = "LazyStudio",
}) {
  const inviterPct =
    REFERRAL_INVITER_PERCENT_BY_PLAN[String(planKey || "free").toLowerCase()] ??
    REFERRAL_INVITER_PERCENT_BY_PLAN.free;
  const name = inviterName && inviterName !== "You" ? inviterName : "I";
  const friendBonus = REFERRAL_FRIEND_BONUS_PERCENT;
  const isGeneral = !toolBlurb || toolBlurb === "LazyStudio";
  const toolPhrase = isGeneral ? "LazyStudio" : `LazyStudio ${toolLabel}`;
  const focusLine = isGeneral
    ? "edit product photos with AI"
    : `try ${toolBlurb} with AI`;

  return {
    whatsapp: {
      id: "whatsapp",
      label: "WhatsApp",
      mark: "WA",
      headline: "Share with sellers on WhatsApp",
      tip: "Short and personal works best in chats and groups.",
      cta: "Share on WhatsApp",
      mode: "open",
      message: `${name} here — ${focusLine}. Use my invite and get ${friendBonus}% extra credits on your first plan: ${inviteUrl}`,
      buildUrl: (message) =>
        `https://wa.me/?text=${encodeURIComponent(message)}`,
    },
    instagram: {
      id: "instagram",
      label: "Instagram",
      headline: "Post or DM on Instagram",
      tip: "We’ll copy your caption — paste it in a Story, Reel, or DM.",
      cta: "Copy & open Instagram",
      mode: "copy-open",
      openUrl: SOCIAL_LINKS.instagram,
      message: `${toolPhrase} — ${focusLine}.\n\nInvite perk: ${friendBonus}% extra credits on your first subscription.\n\n${inviteUrl}`,
    },
    twitter: {
      id: "twitter",
      label: "X / Twitter",
      headline: "Post on X",
      tip: "Keep it punchy — under 280 characters travels farther.",
      cta: "Share on X",
      mode: "open",
      message: isGeneral
        ? `LazyStudio = AI product photos, fast. Get ${friendBonus}% extra credits on your first plan with my invite: ${inviteUrl}`
        : `${toolLabel} on LazyStudio — ${focusLine}. ${friendBonus}% extra credits on your first plan: ${inviteUrl}`,
      buildUrl: (message) =>
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
    },
    youtube: {
      id: "youtube",
      label: "YouTube",
      headline: "Share in a YouTube description or comment",
      tip: "Copy the blurb, then paste under your video or community post.",
      cta: "Copy & open YouTube",
      mode: "copy-open",
      openUrl: SOCIAL_LINKS.youtube,
      message: `Editing with ${toolPhrase}.\nFriends who subscribe via my invite get ${friendBonus}% extra credits: ${inviteUrl}`,
    },
    email: {
      id: "email",
      label: "Email",
      mark: "@",
      headline: "Invite by email",
      tip: "Edit the note, then send — great for store owners and teammates.",
      cta: "Open email app",
      mode: "open",
      subject: isGeneral
        ? "Try LazyStudio — extra credits with my invite"
        : `Try ${toolLabel} on LazyStudio — extra credits with my invite`,
      message: `Hi,\n\nI've been using LazyStudio to ${focusLine}.\n\nIf you sign up with my invite, you get ${friendBonus}% extra credits on your first subscription — and I earn ${inviterPct}% when you do.\n\nStart here:\n${inviteUrl}\n\n— ${name}`,
      buildUrl: (message, subject) =>
        `mailto:?subject=${encodeURIComponent(
          subject || "Join me on LazyStudio"
        )}&body=${encodeURIComponent(message)}`,
    },
    telegram: {
      id: "telegram",
      label: "Telegram",
      mark: "TG",
      headline: "Share on Telegram",
      tip: "Works well in seller groups and 1:1 chats.",
      cta: "Share on Telegram",
      mode: "open",
      message: `${toolPhrase} invite — ${friendBonus}% extra credits on your first plan: ${inviteUrl}`,
      buildUrl: (message) =>
        `https://t.me/share/url?url=${encodeURIComponent(
          inviteUrl
        )}&text=${encodeURIComponent(message)}`,
    },
    sms: {
      id: "sms",
      label: "SMS",
      mark: "SMS",
      headline: "Send a text invite",
      tip: "Keep it short — one clear benefit + your link.",
      cta: "Open Messages",
      mode: "open",
      message: `Try ${isGeneral ? "LazyStudio" : toolLabel} — ${friendBonus}% extra credits on your first plan: ${inviteUrl}`,
      buildUrl: (message) =>
        `sms:?&body=${encodeURIComponent(message)}`,
    },
  };
}
